/**
 * Custom hook to handle investor organization submission
 * Consolidates create and update org mutation logic with shareholder invitation
 * Investor-specific version with shareholder/director mapping
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { countryNameToCodeMap } from "@funded-here-interface/common/src/constant/country";
import {
  KYCStatus,
  Persona,
  EntityTypeEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { ThanksPageType } from "../../../constants/enums";
import { navigateToThanksPage } from "../../ThanksPage/ThanksPageUtils";
import useCreateOrg from "../../../hooks/useCreateOrg";
import useUpdateOrg from "../../../hooks/useUpdateOrg";
import useInviteUserToOrg from "../../../hooks/useInviteUserToOrg";
import {
  buildInvestorBankObject,
  buildInvestmentPreferences,
  buildCreateInvestorOrgData,
  buildUpdateInvestorOrgData,
  InvestorBizState,
  InvestorRepState,
  InvestorBankState,
} from "../utils/investorOrgDataBuilder";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";

interface UseInvestorOrgSubmissionParams {
  setIsLoading: (loading: boolean) => void;
  onError: (error: Error) => void;
}

/**
 * Hook to handle investor organization submission (create or update)
 * Eliminates ~140 lines of duplicate mutation logic
 */
export const useInvestorOrgSubmission = ({
  setIsLoading,
  onError,
}: UseInvestorOrgSubmissionParams) => {
  const navigate = useNavigate();
  const createOrgMutation = useCreateOrg();
  const updateOrgMutation = useUpdateOrg();
  const inviteUserMutation = useInviteUserToOrg();

  /**
   * Invite shareholders/directors to organization
   */
  const inviteShareholder = useCallback(
    (onBoardingUserType: Persona, orgId: number, token: string) => {
      const isSpvOrg = false;
      inviteUserMutation.mutate(
        {
          token,
          orgId,
          onBoardingUserType,
          isSpvOrg,
        },
        {
          onSuccess: () => {
            setIsLoading(false);
          },
          onError(e) {
            setIsLoading(false);
            onError(e as Error);
          },
        }
      );
    },
    [inviteUserMutation, setIsLoading, onError]
  );

  /**
   * Submit investor organization (create or update based on KYC status)
   */
  const submitInvestorOrganization = useCallback(
    ({
      kycStatus,
      orgId,
      token,
      onBoardingUserType,
      entityType,
      bizState,
      repState,
      bankState,
      documentsUpload,
    }: {
      kycStatus: string | null;
      orgId: string | null;
      token: string;
      onBoardingUserType: string;
      entityType: string;
      bizState: InvestorBizState;
      repState: InvestorRepState;
      bankState: InvestorBankState;
      documentsUpload: DocumentsUpload;
    }) => {
      // Build bank object
      const bank = buildInvestorBankObject(bankState);

      // Build investment preferences
      const investmentPreferences = buildInvestmentPreferences(
        onBoardingUserType,
        bizState.investmentAmount
      );

      // Get country code
      let countryCode: string | null = null;
      if (bizState.countryOfDomicile) {
        countryCode = countryNameToCodeMap.get(bizState.countryOfDomicile) || null;
      }

      setIsLoading(true);

      const baseParams = {
        bizState,
        repState,
        bank,
        token,
        onBoardingUserType,
        countryCode,
        documentsUpload,
        investmentPreferences,
      };

      // If KYC is pending resubmission, update existing org
      if (kycStatus === KYCStatus.PENDING_RESUBMISSION) {
        const updateData = buildUpdateInvestorOrgData(baseParams);

        updateOrgMutation.mutate(
          {
            id: orgId?.toString() || "",
            data: updateData,
          },
          {
            onSuccess: (data) => {
              setIsLoading(false);

              if (onBoardingUserType === Persona.INVESTOR) {
                inviteShareholder(Persona.INVESTOR, data.id, token);
                navigateToThanksPage(navigate, ThanksPageType.RESUBMITTED);
              } else if (onBoardingUserType === Persona.ISPV) {
                inviteShareholder(Persona.ISPV, data.id, token);
                navigateToThanksPage(navigate, ThanksPageType.RESUBMITTED);
              }
            },
            onError: (e) => {
              setIsLoading(false);
              onError(e as Error);
            },
          }
        );
      }
      // Otherwise, create new org
      else {
        const createData = buildCreateInvestorOrgData(baseParams);

        createOrgMutation.mutate(createData, {
          onSuccess: (data) => {
            setIsLoading(false);

            if (onBoardingUserType === Persona.INVESTOR) {
              if (entityType === EntityTypeEnum.INSTITUTION) {
                inviteShareholder(Persona.INVESTOR, data.id, token);
                navigateToThanksPage(navigate, ThanksPageType.ONBOARDING);
              } else {
                navigateToThanksPage(navigate, ThanksPageType.ONBOARDING);
              }
            } else if (onBoardingUserType === Persona.ISPV) {
              inviteShareholder(Persona.ISPV, data.id, token);
              navigateToThanksPage(navigate, ThanksPageType.ONBOARDING);
            }
          },
          onError: (e) => {
            setIsLoading(false);
            onError(e as Error);
          },
        });
      }
    },
    [
      navigate,
      createOrgMutation,
      updateOrgMutation,
      setIsLoading,
      onError,
      inviteShareholder,
    ]
  );

  return { submitInvestorOrganization };
};
